import { useQuery } from '@tanstack/react-query';

import type {
  GetTeamByIdResponse,
  GetTeamsRequest,
  GetTeamsResponse,
} from '@mai/platform-api/src/routes/teams.schemas';

import { type QueryOptions, apiClient } from './axios';

// API functions
export const fetchTeams = async (
  params?: GetTeamsRequest,
): Promise<GetTeamsResponse> => {
  const response = await apiClient.get('/teams', { params });
  return response.data;
};

export const fetchTeamById = async (
  teamId: string,
): Promise<GetTeamByIdResponse> => {
  const response = await apiClient.get(`/teams/${teamId}`);
  return response.data;
};

export const fetchTeamsByIds = async (
  teamIds: string[],
): Promise<GetTeamsResponse> => {
  if (teamIds.length === 0)
    return {
      items: [],
      pagination: {
        page: 1,
        pageSize: 0,
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  return fetchTeams({ teamIds });
};

// React Query hooks
export const useTeams = (params?: GetTeamsRequest, options?: QueryOptions) => {
  return useQuery({
    queryKey: ['teams', params],
    queryFn: () => fetchTeams(params),
    enabled: options?.enabled,
    retry: options?.retry,
  });
};

export const useTeamById = (teamId: string | null, options?: QueryOptions) => {
  return useQuery({
    queryKey: ['teams', teamId],
    queryFn: () => fetchTeamById(teamId!),
    enabled: !!teamId && options?.enabled !== false,
    retry: options?.retry,
  });
};

export const useTeamsByIds = (teamIds: string[], options?: QueryOptions) => {
  return useQuery({
    queryKey: ['teams', 'by-ids', teamIds.sort()],
    queryFn: () => fetchTeamsByIds(teamIds),
    enabled: teamIds.length > 0 && options?.enabled !== false,
    retry: options?.retry,
  });
};
